import { Typography } from '@blofin/blofin-ui';
import clsx from 'clsx';
import ClassifyTag from '../ClassifyTag';
import Card, { CardContent, CardMedia } from '../Card';
import { useDevice } from 'hooks/activityCenter/useDevice';
import { useTranslations } from 'next-intl';
import useCurrentLocale from 'hooks/useCurrentLocale';
import useActivityStatus from 'hooks/activityCenter/useActivityStatus';
import styles from './card.module.scss';
import { formatDate } from 'utils/format';
import { useRouter } from 'next/router';

export default function ActivityCard({
  activity_title,
  activity_sub_title,
  state,
  start_time,
  end_time,
  tags,
  banner_link,
  activity_link,
  type,
  timeLabel,
  timeLabelString,
}: ActivityItem) {
  const { isMobile } = useDevice();
  const t = useTranslations('activity');
  const { localeLink } = useCurrentLocale();
  const { locale } = useRouter();

  return (
    <a href={`${type === 'REWARDS' ? '' : '/activity'}${localeLink}${activity_link}`} target="_blank" rel="noreferrer">
      <Card className="relative">
        <CardMedia
          url={banner_link}
          className="w-full md:-mt-[1px] md:h-[240px] md:w-[480px] md:min-w-[480px] md:rounded-l-[16px]"
        />
        <CardContent className="relative flex grow flex-col rounded-b-[16px] border border-t-0 border-light-line-secondary p-[16px] md:rounded-none md:border-none md:p-[32px]">
          <div className="mt-0 flex gap-[8px]">
            {tags?.slice(0, 3)?.map((tag, i) => (
              <ClassifyTag key={`${tag}-${i}`} className="rounded-[4px] text-sm capitalize leading-[18px]">
                {tag}
              </ClassifyTag>
            ))}
          </div>

          <Typography
            variant={isMobile ? 'body1' : 'subtitle1'}
            weight="bold"
            className="mt-[8px] line-clamp-2 leading-[24px] text-light-label md:mt-[16px] md:leading-[32px]"
          >
            {activity_title}
          </Typography>

          <Typography
            variant={isMobile ? 'body4' : 'body3'}
            weight="regular"
            className="mt-[8px] line-clamp-3 leading-[18px] text-light-label-60 md:line-clamp-2 md:leading-[20px] md:text-light-label-40"
          >
            {activity_sub_title}
          </Typography>

          <div className="mt-[32px] flex items-center md:mt-auto">
            <i className="iconfont icon-clock-fill hidden text-light-primary md:inline" />
            <Typography
              variant={isMobile ? 'body4' : 'body3'}
              weight="medium"
              className="leading-[18px] text-light-label md:ml-[8px] md:leading-[20px] md:text-light-label-60"
            >
              {`${timeLabel ? timeLabel : t('eventPeriod')} ${
                timeLabelString
                  ? timeLabelString
                  : `${formatDate(start_time, 'yyyy/MM/dd HH:mm', undefined, locale)}-${formatDate(
                      end_time,
                      'yyyy/MM/dd HH:mm',
                      undefined,
                      locale
                    )}`
              }`}
            </Typography>
          </div>
        </CardContent>
        <Status state={state} />
      </Card>
    </a>
  );
}

interface StatusBadgeProps {
  state: ActivityState;
  className?: string;
}

const StatusBadge = ({ state, className }: StatusBadgeProps) => {
  const colorMap = {
    ON_GOING: {
      bg1: '#00CC5D',
      bg2: '#00A54B',
    },
    COMING_SOON: {
      bg1: '#FD4285',
      bg2: '#E91963',
    },
    ACCEPTED: {
      bg1: '#FFC400',
      bg2: '#E1B00C',
    },
    EXPIRED: {
      bg1: '#CACBCC',
      bg2: '#B1B2B2',
    },
  };

  const { bg1, bg2 } = colorMap[state];

  return (
    <svg
      width="170"
      height="43"
      viewBox="0 0 170 43"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M160.874 42.9156L170 40.0427V31.5427L160.874 31.6272V42.9156Z"
        fill={bg2}
      />
      <path d="M0 24C0 10.7452 10.7452 0 24 0H170V40H0V24Z" fill={bg1} />
    </svg>
  );
};

function Status({ state }: Record<'state', ActivityState>) {
  const t = useTranslations('activity');
  const { text, iconClassName } = useActivityStatus(state);

  return (
    <>
      <div className={'absolute -right-[10px] -top-2 hidden h-[43px] w-[170px] md:flex'}>
        <StatusBadge state={state} className="h-full w-full" />
        <div className="absolute left-0 top-0 flex h-[calc(100%-3px)] w-full items-center gap-2 pl-5">
          <i className={clsx('iconfont text-lg text-light-background md:text-[24px]', iconClassName)} />
          <span className="text-lg font-medium capitalize leading-[26px] text-light-background">{t(text)}</span>
        </div>
      </div>
      <div className={clsx(styles.status, styles[text], 'flex md:hidden')}>
        <i className={clsx('iconfont mx-[8px] text-lg md:text-[24px]', iconClassName)} />
        {t(text)}
      </div>
    </>
  );
}
